import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  User,
  StickyNote,
  Image as ImageIcon,
  Type,
  CalendarClock,
} from "lucide-react";
import { adminApiRequest } from "../utils/adminApi";

const BASE_UPLOAD_URL = "https://kinewsstudiolimited.co.uk";

export default function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [markingReadyId, setMarkingReadyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const visibleOrders = useMemo(() => {
    return [...orders].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [orders]);

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUploadUrl = (filePath) => {
    if (!filePath) return "";
    if (/^https?:\/\//i.test(filePath)) return filePath;
    return `${BASE_UPLOAD_URL}/${String(filePath).replace(/^\/+/, "")}`;
  };

  const toggleExpanded = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await adminApiRequest({
        script: "fetch_shop_orders",
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load orders.");
      }

      setOrders(Array.isArray(res.orders) ? res.orders : []);
    } catch (err) {
      setError(err.message || "Unable to load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkReady = async (orderId) => {
    try {
      setMarkingReadyId(orderId);
      setError("");
      setSuccessMessage("");

      const res = await adminApiRequest({
        script: "mark_shop_order_ready",
        data: { order_id: orderId },
      });

      if (!res?.success) {
        throw new Error(res?.message || "Failed to update order.");
      }

      await loadOrders();

      setSuccessMessage("Order marked as ready.");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setError(err.message || "Unable to mark order as ready.");
    } finally {
      setMarkingReadyId(null);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  return (
    <section className="h-full min-h-0">
      <div className="flex h-full min-h-0 flex-col rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <h2 className="text-2xl font-semibold text-slate-900">Shop Orders</h2>
          <p className="mt-1 text-sm text-slate-500">
            Manage paid orders and contact customers quickly.
          </p>
        </div>

        {error && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}

        {successMessage && (
          <div className="px-5 pt-4">
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {successMessage}
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-5">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading orders...
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
              <p className="text-sm text-slate-500">No new shop orders.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
              {visibleOrders.map((order) => {
                const isExpanded = expandedOrderId === order.id;
                const status = String(order.status || "").toLowerCase();

                const statusConfig = {
                  pending: {
                    label: "Pending",
                    className: "bg-yellow-100 text-yellow-700",
                  },
                  processing: {
                    label: "Processing",
                    className: "bg-blue-100 text-blue-700 animate-pulse",
                  },
                  completed: {
                    label: "Ready",
                    className: "bg-green-100 text-green-700",
                  },
                  cancelled: {
                    label: "Cancelled",
                    className: "bg-red-100 text-red-700",
                  },
                };

                const currentStatus = statusConfig[status] || {
                  label: status || "Unknown",
                  className: "bg-slate-100 text-slate-700",
                };

                const isReady = status === "completed";

                return (
                  <div
                    key={order.id}
                    onClick={() => toggleExpanded(order.id)}
                    className={`cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition ${
                      isReady
                        ? "border-green-200 bg-green-50/70 ring-1 ring-green-100"
                        : "border-slate-200 bg-white hover:shadow-md"
                    }`}
                  >
                    <div className="p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div className="inline-flex min-w-0 items-center gap-1.5 text-xs font-medium text-slate-500">
                          <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {formatDateTime(order.created_at)}
                          </span>
                        </div>

                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${currentStatus.className}`}
                        >
                          {currentStatus.label}
                        </span>
                      </div>

                      {isReady && (
                        <div className="mb-3 inline-flex items-center rounded-full bg-green-600 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                          Ready for Collection
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-base font-semibold text-slate-900">
                            {order.order_number}
                          </div>

                          <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                            <User className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {order.customer_name}
                            </span>
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <div className="text-lg font-semibold text-slate-900">
                            £{Number(order.total || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {isReady && order.ready_at && (
                        <div className="mt-3 rounded-xl border border-green-200 bg-white px-3 py-2 text-sm text-green-700">
                          Ready at:{" "}
                          <span className="font-semibold">
                            {formatDateTime(order.ready_at)}
                          </span>
                        </div>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkReady(order.id);
                          }}
                          disabled={isReady || markingReadyId === order.id}
                          className={`inline-flex h-10 items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-white transition ${
                            isReady || markingReadyId === order.id
                              ? "cursor-not-allowed bg-slate-400"
                              : "bg-green-600 hover:bg-green-700"
                          }`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          <span>
                            {markingReadyId === order.id
                              ? "Updating..."
                              : isReady
                                ? "Ready"
                                : "Mark Ready"}
                          </span>
                        </button>

                        {order.customer_phone ? (
                          <a
                            href={`tel:${order.customer_phone}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-3 text-slate-700 transition hover:bg-slate-50"
                            title="Call Customer"
                          >
                            <Phone className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 px-3 text-slate-400"
                            title="Call Customer"
                          >
                            <Phone className="h-4 w-4" />
                          </button>
                        )}

                        {order.customer_email ? (
                          <a
                            href={`mailto:${order.customer_email}?subject=${encodeURIComponent(
                              `Update on your order ${order.order_number}`,
                            )}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 px-3 text-slate-700 transition hover:bg-slate-50"
                            title="Email Customer"
                          >
                            <Mail className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 px-3 text-slate-400"
                            title="Email Customer"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(order.id);
                          }}
                          className="ml-auto inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-indigo-200 px-3 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50"
                        >
                          <span>{isExpanded ? "Hide" : "Details"}</span>
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 border-t border-slate-200 pt-4">
                          {order.notes && (
                            <div className="mb-4 rounded-xl bg-slate-50 p-3">
                              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                <StickyNote className="h-3.5 w-3.5" />
                                Order Notes
                              </div>
                              <div className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                                {order.notes}
                              </div>
                            </div>
                          )}

                          {Array.isArray(order.items) &&
                          order.items.length > 0 ? (
                            <div className="space-y-3">
                              {order.items.map((item) => (
                                <div
                                  key={item.id}
                                  className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <h4 className="truncate text-sm font-semibold text-slate-900">
                                        {item.product_name}
                                      </h4>
                                      <div className="mt-1 text-sm text-slate-600">
                                        Qty:{" "}
                                        <span className="font-medium text-slate-900">
                                          {item.qty}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="shrink-0 text-right">
                                      <div className="text-sm font-semibold text-slate-900">
                                        £
                                        {Number(item.line_total || 0).toFixed(
                                          2,
                                        )}
                                      </div>
                                      <div className="text-[11px] text-slate-500">
                                        Item total
                                      </div>
                                    </div>
                                  </div>

                                  {item.custom_text &&
                                    String(item.custom_text).trim() !== "" &&
                                    String(item.custom_text).trim() !== "0" && (
                                      <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2">
                                        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                          <Type className="h-3.5 w-3.5" />
                                          {item.custom_text_label ||
                                            "Custom Text"}
                                        </div>
                                        <div className="mt-1 whitespace-pre-wrap text-sm text-indigo-600">
                                          {item.custom_text}
                                        </div>
                                      </div>
                                    )}

                                  {Array.isArray(item.options) &&
                                    item.options.length > 0 && (
                                      <div className="mt-3">
                                        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                          Selected Options
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {item.options.map((opt, index) => (
                                            <span
                                              key={`${item.id}-opt-${index}`}
                                              className="rounded-full bg-white px-2.5 py-1 text-xs text-slate-700"
                                            >
                                              {opt.option_label}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                  {Array.isArray(item.uploads) &&
                                    item.uploads.length > 0 && (
                                      <div className="mt-3">
                                        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                          <ImageIcon className="h-3.5 w-3.5" />
                                          Uploaded Files
                                        </div>

                                        <div className="mt-2 grid grid-cols-3 gap-2">
                                          {item.uploads.map((upload, index) => (
                                            <a
                                              key={`${item.id}-upload-${index}`}
                                              href={getUploadUrl(
                                                upload.file_path,
                                              )}
                                              target="_blank"
                                              rel="noreferrer"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                              className="group overflow-hidden rounded-xl border border-slate-200 bg-white"
                                            >
                                              <div className="aspect-square bg-slate-100">
                                                <img
                                                  src={getUploadUrl(
                                                    upload.file_path,
                                                  )}
                                                  alt={
                                                    upload.original_name ||
                                                    `Upload ${index + 1}`
                                                  }
                                                  className="h-full w-full object-cover transition duration-200 group-hover:scale-[1.03]"
                                                />
                                              </div>
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                              No items found for this order.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
